import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useWeibullChartOptions } from './useWeibullChartOptions';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

interface WeibullProbabilityPlotProps {
  analysis: WeibullAnalysisResult;
  showConfidenceIntervals?: boolean;
  confidenceLevel?: number;
  onToggleConfidenceIntervals?: (show: boolean) => void;
  onConfidenceLevelChange?: (level: number) => void;
  showCensoredData?: boolean;
  metadata?: {
    totalSamples: number;
    failureCount: number;
    censoredCount: number;
    censoringPercentage: number;
    hasCensoredData: boolean;
  } | null;
  censoredData?: CensoredDataPoint[];
  height?: number;
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
}

const WeibullProbabilityPlot: React.FC<WeibullProbabilityPlotProps> = ({
  analysis,
  showConfidenceIntervals = true,
  confidenceLevel = 95,
  onToggleConfidenceIntervals: _onToggleConfidenceIntervals,
  onConfidenceLevelChange: _onConfidenceLevelChange,
  showCensoredData = true,
  censoredData,
  height: _height = 400,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
}) => {
  const options = useWeibullChartOptions({
    analysis,
    showConfidenceIntervals,
    confidenceLevel,
    showCensoredData,
    censoredData,
    chartType: 'probability',
    colorBy,
    markerBy,
    sizeBy,
    showLegend,
  });

  // Force re-render when confidence intervals change
  const chartKey = React.useMemo(
    () =>
      `probability-${showConfidenceIntervals}-${confidenceLevel}-${showCensoredData}-${Date.now()}`,
    [showConfidenceIntervals, confidenceLevel, showCensoredData]
  );

  return (
    <div className="w-full h-full">
      <ReactECharts
        key={chartKey}
        option={options}
        style={{ height: '100%', width: '100%' }}
        theme="light"
        opts={{ renderer: 'canvas' }}
        notMerge={true}
        lazyUpdate={false}
      />
    </div>
  );
};

export default WeibullProbabilityPlot;
